import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';

function Contribute() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [channels, setChannels] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const getToken = () => localStorage.getItem('accessToken');
  const headers = { Authorization: `Bearer ${getToken()}` };

  useEffect(() => {
    if (user) {
      fetchStatus();
      fetchChannels();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchStatus = async () => {
    try {
      const res = await fetch('/api/contribute/status', { headers });
      const data = await res.json();
      setStatus(data);
    } catch (e) {}
    setLoading(false);
  };

  const fetchChannels = async () => {
    try {
      const res = await fetch('/api/contribute/channels', { headers });
      const data = await res.json();
      setChannels(data.channels || []);
    } catch (e) {}
  };

  const handleChannelToggle = (channelId) => {
    setSelectedChannels(prev =>
      prev.includes(channelId)
        ? prev.filter(id => id !== channelId)
        : [...prev, channelId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (selectedChannels.length === 0) {
      setError('Please select at least one channel');
      return;
    }
    if (reason.trim().length < 200) {
      setError('Please write at least 200 characters about why you want to contribute');
      return;
    }
    if (reason.trim().length > 500) {
      setError('Please keep your response under 500 characters');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch('/api/contribute/apply', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ channelIds: selectedChannels, reason: reason.trim() })
      });
      const data = await res.json();
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError(data.error || 'Failed to submit application');
      }
    } catch (e) {
      setError('Network error. Please try again.');
    }
    setSubmitting(false);
  };

  // Get unique categories from channels
  const categories = [...new Map(channels.map(ch => [ch.category_id, { category_id: ch.category_id, category_name: ch.category_name }])).values()];

  // Filter channels by selected category
  const filteredChannels = selectedCategory
    ? channels.filter(ch => ch.category_id === parseInt(selectedCategory))
    : [];

  if (loading) {
    return (
      <div>
        <Navbar />
        <div style={{ maxWidth: '700px', margin: '0 auto', padding: '24px', textAlign: 'center' }}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Not logged in
  if (!user) {
    return (
      <div>
        <Navbar />
        <div style={{ maxWidth: '700px', margin: '0 auto', padding: '24px' }}>
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <h1 style={{ fontSize: '2rem', marginBottom: '16px' }}>🌱 Become a Community Contributor</h1>
            <p style={{ fontSize: '1.1rem', color: '#4b5563', marginBottom: '24px', lineHeight: '1.7' }}>
              Help your community thrive by reviewing and creating content for identity spaces you know well.
              As a contributor, you'll help ensure quality discussions and resources for people who share your experiences.
            </p>
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '20px', marginBottom: '24px', textAlign: 'left' }}>
              <h3 style={{ marginBottom: '12px' }}>What contributors do:</h3>
              <ul style={{ lineHeight: '2', color: '#374151' }}>
                <li>✅ Review and approve community posts</li>
                <li>✏️ Edit content to improve quality</li>
                <li>🆕 Create original posts sharing your experience</li>
                <li>🛡️ Help maintain a safe, supportive space</li>
              </ul>
            </div>
            <button
              className="btn btn-primary"
              style={{ background: '#10b981', padding: '14px 28px', fontSize: '1rem' }}
              onClick={() => navigate('/register?returnTo=/contribute')}
            >
              Sign up to Apply →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Already a moderator
  if (status?.status === 'moderator') {
    return (
      <div>
        <Navbar />
        <div style={{ maxWidth: '700px', margin: '0 auto', padding: '24px' }}>
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <h1 style={{ fontSize: '2rem', marginBottom: '16px' }}>🎉 You're already a contributor!</h1>
            <p style={{ color: '#4b5563', marginBottom: '8px' }}>
              You're currently moderating:
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', marginBottom: '24px' }}>
              {status.channels.map(ch => (
                <span key={ch.channel_id} style={{ background: '#ede9fe', color: '#7c3aed', padding: '4px 12px', borderRadius: '8px', fontSize: '14px' }}>
                  {ch.channel_name}
                </span>
              ))}
            </div>
            <button
              className="btn btn-primary"
              style={{ background: '#10b981', padding: '12px 24px' }}
              onClick={() => navigate('/content-studio')}
            >
              Open Content Studio →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Has pending application
  if (status?.status === 'pending') {
    return (
      <div>
        <Navbar />
        <div style={{ maxWidth: '700px', margin: '0 auto', padding: '24px' }}>
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <h1 style={{ fontSize: '2rem', marginBottom: '16px' }}>⏳ Application Under Review</h1>
            <p style={{ color: '#4b5563', marginBottom: '16px', lineHeight: '1.7' }}>
              Your application to become a contributor has been submitted and is being reviewed by our team.
              We'll notify you once a decision is made.
            </p>
            <div style={{ background: '#fefce8', border: '1px solid #fde047', borderRadius: '12px', padding: '16px', textAlign: 'left' }}>
              <p style={{ fontSize: '13px', color: '#854d0e' }}>
                <strong>Submitted:</strong> {new Date(status.application.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Previously rejected — allow re-application
  if (status?.status === 'rejected') {
    // Show the form again but with a note
  }

  // Application submitted successfully
  if (submitted) {
    return (
      <div>
        <Navbar />
        <div style={{ maxWidth: '700px', margin: '0 auto', padding: '24px' }}>
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <h1 style={{ fontSize: '2rem', marginBottom: '16px' }}>🎉 Application Submitted!</h1>
            <p style={{ color: '#4b5563', lineHeight: '1.7' }}>
              Thank you for applying to become a contributor. We'll review your application shortly
              and notify you once a decision is made.
            </p>
            <button
              className="btn btn-primary"
              style={{ marginTop: '24px' }}
              onClick={() => navigate('/dashboard')}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show application form
  return (
    <div>
      <Navbar />
      <div style={{ maxWidth: '700px', margin: '0 auto', padding: '24px' }}>
        <h1 style={{ marginBottom: '8px' }}>🌱 Become a Community Contributor</h1>
        <p style={{ color: '#6b7280', marginBottom: '24px' }}>
          Apply to help moderate and create content for communities you know well.
        </p>

        {status?.status === 'rejected' && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
            <p style={{ color: '#991b1b', fontSize: '14px' }}>
              Your previous application was not approved.
              {status.application?.admin_notes && (
                <span> Feedback: "{status.application.admin_notes}"</span>
              )}
              {' '}You're welcome to apply again.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px', marginBottom: '20px', background: '#fafafa' }}>
            <h3 style={{ marginBottom: '12px' }}>1. Select channels you want to moderate</h3>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
              Choose a category first, then select the specific channels you'd like to contribute to.
            </p>

            <select
              value={selectedCategory}
              onChange={(e) => { setSelectedCategory(e.target.value); }}
              style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db', marginBottom: '12px' }}
            >
              <option value="">Select a category...</option>
              {categories.map(c => (
                <option key={c.category_id} value={c.category_id}>{c.category_name}</option>
              ))}
            </select>

            {filteredChannels.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '200px', overflowY: 'auto', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '8px', background: '#fff' }}>
                {filteredChannels.map(ch => (
                  <label key={ch.channel_id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '6px 8px', borderRadius: '6px', background: selectedChannels.includes(ch.channel_id) ? '#f0fdf4' : 'transparent' }}>
                    <input
                      type="checkbox"
                      checked={selectedChannels.includes(ch.channel_id)}
                      onChange={() => handleChannelToggle(ch.channel_id)}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <span style={{ fontSize: '14px' }}>{ch.name}</span>
                  </label>
                ))}
              </div>
            )}

            {selectedChannels.length > 0 && (
              <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {selectedChannels.map(id => {
                  const ch = channels.find(c => c.channel_id === id);
                  return ch ? (
                    <span key={id} style={{ background: '#dcfce7', color: '#166534', padding: '3px 10px', borderRadius: '6px', fontSize: '13px' }}>
                      {ch.name} ✕
                      <span onClick={() => handleChannelToggle(id)} style={{ cursor: 'pointer', marginLeft: '4px' }}></span>
                    </span>
                  ) : null;
                })}
              </div>
            )}
          </div>

          <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px', marginBottom: '20px', background: '#fafafa' }}>
            <h3 style={{ marginBottom: '12px' }}>2. Why are you a good fit?</h3>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
              Share your experience with this community. What makes you qualified to review and create content?
            </p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 500))}
              maxLength={500}
              placeholder="I've been part of this community for... I have experience with... I want to help because..."
              style={{ width: '100%', minHeight: '120px', padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db', fontFamily: 'inherit', fontSize: '14px', lineHeight: '1.6', resize: 'vertical' }}
            />
            <p style={{ fontSize: '12px', color: reason.trim().length >= 200 ? '#10b981' : '#9ca3af', marginTop: '6px' }}>
              {reason.trim().length}/500 characters (200 minimum)
            </p>
          </div>

          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
              <p style={{ color: '#dc2626', fontSize: '14px' }}>{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="btn btn-primary"
            style={{ width: '100%', padding: '14px', background: submitting ? '#9ca3af' : '#10b981', fontSize: '1rem', cursor: submitting ? 'not-allowed' : 'pointer' }}
          >
            {submitting ? 'Submitting...' : 'Submit Application'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default Contribute;
